import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import HeroSection from '@/components/home/HeroSection';
import UpcomingEventsSection from '@/components/home/UpcomingEventsSection';
import HealthProfessionalCTA from '@/components/home/HealthProfessionalCTA';
import OrganizerCTA from '@/components/home/OrganizerCTA';
import { Event, ViewMode } from '@/lib/types';
import { useAuth } from '@/contexts/AuthContext';
import RecommendedEventsSection from '@/components/home/RecommendedEventsSection';
import { getPersonalizedEvents } from '@/utils/recommendationUtils';
import { getMLRecommendedEvents } from '@/services/mlRecommendationService';
import LoggedInHeroSection from '@/components/home/LoggedInHeroSection';
import { useToast } from '@/hooks/use-toast';
import { fetchMyEvents } from '@/services/eventService';
import EventDetailsDrawer from '@/components/EventDetailsDrawer';
import { UserRoles } from '@/contexts/auth/enums';
import EventMetaTags from '@/components/EventMetaTags';
import SurveyModal from '@/components/SurveyModal';
import CalendarPreviewSection from '@/components/home/CalendarPreviewSection';
import { sendPageView, sendEventDetailView } from '@/services/analyticsService';

const Index = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const { user, isAuthenticated } = useAuth();
  const [events, setEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [recommendedEvents, setRecommendedEvents] = useState<Event[]>([]);
  const [mlRecommendedEvents, setMlRecommendedEvents] = useState<Event[]>([]);
  const { toast } = useToast();
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [isEventDrawerOpen, setIsEventDrawerOpen] = useState(false);

  const { eventSlug } = useParams<{ eventSlug: string }>();
  const navigate = useNavigate();

  const isOrganizer = user?.roles?.includes(UserRoles.ORGANIZER);

  // Oldal megtekintés esemény küldése
  useEffect(() => {
    sendPageView('Főoldal');
  }, []);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        setIsLoading(true);
        const apiEvents = await fetchMyEvents();
        setEvents(apiEvents);

        if (isAuthenticated && user && !isOrganizer) {
          const personalized = getPersonalizedEvents(apiEvents, user);
          setRecommendedEvents(personalized);

          const mlRecommendations = getMLRecommendedEvents(apiEvents, user, 6);
          setMlRecommendedEvents(mlRecommendations);

          console.log(`Loaded ${mlRecommendations.length} ML recommendations and ${personalized.length} basic recommendations from API events`);
        }
      } catch (error) {
        console.error('Error loading events and recommendations:', error);
        toast({
          title: "Hiba történt",
          description: "Az események betöltése sikertelen",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadEvents();
  }, [user, isAuthenticated, toast, isOrganizer]);

  useEffect(() => {
    if (eventSlug && events.length > 0) {
      const event = findEventBySlugOrCode(eventSlug, events);

      if (event) {
        setSelectedEvent(event);
        setIsEventDrawerOpen(true);
        
        // Analytics esemény: esemény részletes megtekintése
        sendEventDetailView(event);
      }
    }
  }, [eventSlug, events]);

  const findEventBySlugOrCode = (slugOrCode: string, eventsList: Event[]): Event | undefined => {
    const baseSlugOrCode = slugOrCode.split('-')[0];

    return eventsList.find(e =>
      (e.slug && e.slug === baseSlugOrCode) ||
      (e.code && e.code === baseSlugOrCode) ||
      (e.slug && slugOrCode.startsWith(e.slug))
    );
  };

  const handleEventClick = (event: Event) => {
    setSelectedEvent(event);
    setIsEventDrawerOpen(true);
    
    // Analytics esemény: esemény részletes megtekintése - garantáljuk a küldést
    sendEventDetailView(event);

    const eventIdentifier = event.slug || event.code;
    if (eventIdentifier) {
      const preventScrollState = { preventScroll: true };
      navigate(`/event/${eventIdentifier}`, {
        replace: true,
        state: preventScrollState
      });
    }
  };

  const handleDrawerClose = () => {
    setIsEventDrawerOpen(false);

    if (eventSlug) {
      navigate('/', {
        replace: true,
        state: { preventScroll: true }
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      {selectedEvent && <EventMetaTags event={selectedEvent} />}

      <Navbar />

      {isAuthenticated ? (
        <>
          <LoggedInHeroSection />

          {!isOrganizer && (
            <>
              {(mlRecommendedEvents.length > 0 || isLoading) && (
                <div className="container mx-auto px-4">
                  <RecommendedEventsSection
                    events={mlRecommendedEvents}
                    isLoading={isLoading}
                    title="Ajánlások neked"
                    onEventClick={handleEventClick}
                  />
                </div>
              )}

              {mlRecommendedEvents.length === 0 && (recommendedEvents.length > 0 || isLoading) && (
                <div className="container mx-auto px-4">
                  <RecommendedEventsSection
                    events={recommendedEvents}
                    isLoading={isLoading}
                    onEventClick={handleEventClick}
                  />
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <>
          <HeroSection />
          <CalendarPreviewSection events={events} isLoading={isLoading} />
        </>
      )}

      <UpcomingEventsSection events={events} onEventClick={handleEventClick} />

      {!isAuthenticated && (
        <>
          <HealthProfessionalCTA />
          <OrganizerCTA />
        </>
      )}
      <Footer />

      <EventDetailsDrawer
        event={selectedEvent}
        open={isEventDrawerOpen}
        onClose={handleDrawerClose}
      />

      {/* Visszajelzési űrlap komponens - csak egyszer jelenik meg */}
      <SurveyModal delayMs={30000} dismissDays={30} />
    </div>
  );
};

export default Index;
